import type { IHubRepository } from "@/application/ports/repositories";
import type {
  HubDashboardSummary,
  HubInvestment,
  HubInvestmentListParams,
  HubInvestmentListResponse,
  HubInvestorProfile,
  HubPendingActions,
  HubPendingActionsParams,
  HubProfilesResponse,
  HubProfileUpdateRequest,
  HubSigningUrlRequest,
  HubSigningUrlResponse,
} from "@/domain/hub";
import type {
  InvestmentDocumentResponse,
  DocumentDownloadResponse,
  DocumentWithReviewResponse,
  PaginatedBlocksResponse,
} from "@/domain/documentBlocks";
import type { Language } from "@/domain/common";
import { downloadFromSignedUrl } from "../fileDownload";
import { getHubProfileStorageService } from "@/infrastructure/di/services";
import type {
  HubInvestmentDetailResponse,
  HubInvestmentResponse,
  InvestorProfileResponse,
} from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

// The backend fills server-side defaults for several hub fields but the spec
// marks them optional, hence the localized casts below (see backend wishlist).

export class HubRepository implements IHubRepository {
  private getSelectedProfileUuid(): string | undefined {
    try {
      return getHubProfileStorageService().getSelectedProfileUuid() ?? undefined;
    } catch {
      return undefined;
    }
  }

  private computeLegalRepName(firstName?: string | null, lastName?: string | null): string | null {
    if (!firstName && !lastName) return null;
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  private transformHubInvestment(
    investment: HubInvestmentResponse | HubInvestmentDetailResponse,
  ): HubInvestment {
    return investment;
  }

  private toLanguage(value: string): Language {
    return value === "en" || value === "ca" ? value : "es";
  }

  private transformHubProfile(profile: InvestorProfileResponse): HubInvestorProfile {
    return {
      ...profile,
      communication_language: this.toLanguage(profile.communication_language),
      legal_rep_name: this.computeLegalRepName(
        profile.legal_rep_first_name,
        profile.legal_rep_last_name,
      ),
    };
  }

  async getProfiles(): Promise<HubProfilesResponse> {
    return unwrap(sdk.listMyProfiles());
  }

  async getProfile(investorUuid?: string): Promise<HubInvestorProfile> {
    const profileUuid = investorUuid ?? this.getSelectedProfileUuid();
    const profile = await unwrap(sdk.getMyProfile({ query: { investor_uuid: profileUuid } }));
    return this.transformHubProfile(profile);
  }

  async updateProfile(data: HubProfileUpdateRequest): Promise<HubInvestorProfile> {
    const profile = await unwrap(sdk.updateMyProfile({ body: data }));
    return this.transformHubProfile(profile);
  }

  async getDashboard(): Promise<HubDashboardSummary> {
    const profileUuid = this.getSelectedProfileUuid();
    const dashboard = await unwrap(sdk.getMyDashboard({ query: { investor_uuid: profileUuid } }));

    return {
      ...dashboard,
      recent_investments: (dashboard.recent_investments ?? []).map((inv) =>
        this.transformHubInvestment(inv),
      ),
    };
  }

  async getPendingActions(params: HubPendingActionsParams = {}): Promise<HubPendingActions> {
    const profileUuid = this.getSelectedProfileUuid();
    const actions = await unwrap(
      sdk.getMyPendingActions({
        query: { expiring_days: params.expiring_days, investor_uuid: profileUuid },
      }),
    );
    return actions;
  }

  async getInvestments(params: HubInvestmentListParams = {}): Promise<HubInvestmentListResponse> {
    const profileUuid = this.getSelectedProfileUuid();
    const response = await unwrap(
      sdk.listMyInvestments({
        query: { limit: params.limit, offset: params.offset, investor_uuid: profileUuid },
      }),
    );

    const { items, ...rest } = response;
    return {
      ...rest,
      investments: items.map((inv) => this.transformHubInvestment(inv)),
    };
  }

  async getInvestmentDetail(uuid: string): Promise<HubInvestment> {
    const profileUuid = this.getSelectedProfileUuid();
    const investment = await unwrap(
      sdk.getMyInvestment({
        path: { investment_uuid: uuid },
        query: { investor_uuid: profileUuid },
      }),
    );

    return this.transformHubInvestment(investment);
  }

  async listInvestmentDocuments(
    investmentUuid: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedBlocksResponse<InvestmentDocumentResponse>> {
    const profileUuid = this.getSelectedProfileUuid();
    const documents = await unwrap(
      sdk.listMyInvestmentDocuments({
        path: { investment_uuid: investmentUuid },
        query: { limit: params?.limit, offset: params?.offset, investor_uuid: profileUuid },
      }),
    );
    return documents;
  }

  async getDocumentDownloadUrl(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    const profileUuid = this.getSelectedProfileUuid();
    return unwrap(
      sdk.downloadMyInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
        query: { investor_uuid: profileUuid },
      }),
    );
  }

  async downloadDocument(
    investmentUuid: string,
    documentUuid: string,
    filename?: string,
  ): Promise<void> {
    const urlResponse = await this.getDocumentDownloadUrl(investmentUuid, documentUuid);
    await downloadFromSignedUrl(
      urlResponse.signed_url,
      filename || urlResponse.filename || "document",
    );
  }

  async getSigningUrl(
    investmentUuid: string,
    documentUuid: string,
    request: HubSigningUrlRequest,
  ): Promise<HubSigningUrlResponse> {
    const profileUuid = this.getSelectedProfileUuid();
    const signing = await unwrap(
      sdk.getDocumentSigningUrl({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
        query: { investor_uuid: profileUuid },
        body: request,
      }),
    );
    return signing;
  }

  async uploadPaymentReceipt(
    investmentUuid: string,
    file: File,
  ): Promise<DocumentWithReviewResponse> {
    const profileUuid = this.getSelectedProfileUuid();
    const document = await unwrap(
      sdk.uploadPaymentReceipt({
        path: { investment_uuid: investmentUuid },
        query: { investor_uuid: profileUuid },
        body: { file },
      }),
    );
    return document;
  }
}
